import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader,
  Upload,
  ArrowLeft,
  CloudUpload,
  X,
  FileText,
} from "lucide-react";
import ProcessingTimeline from "./processingtimeline";
import { useFile } from "../context/filecontext";
import axios from "axios";
import Header from "../components/header";
import SearchModifiedAnalysis from "./searchmodifiedanalysis";

const FileUploadPage = () => {
  const [stopeId, setStopeId] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [showTimeline, setShowTimeline] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const { setMetadataUrl, setUploadResponseData, setDesignLasUrl } = useFile();
  const [uploadResponse, setUploadResponse] = useState(null);
  const [measurements, setMeasurements] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isCalculationEnabled, setIsCalculationEnabled] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
  // const API_BASE_URL = 'https://aiq2h6wnn7.us-east-1.awsapprunner.com';

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const extension = file.name.split(".").pop().toLowerCase();
      if (!["las", "laz"].includes(extension)) {
        setErrorMessage("Only .las or .laz files are allowed");
        setSelectedFile(null);
        event.target.value = null;
        return;
      }

      setSelectedFile(file);
      setErrorMessage("");
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const handleProcessingComplete = ({
    file_id,
    volume,
    metadata_url,
    octree_url,
    las_url,
  }) => {
    const fullMetadataUrl = metadata_url;
    setMetadataUrl(fullMetadataUrl);
    setDesignLasUrl(las_url);
    setUploadResponseData({
      file_id,
      metadata_url: fullMetadataUrl,
      octree_url: octree_url,
      volume,
    });
    navigate("/viewer", {
      state: {
        volume,
        file_id,
        metadata_url: fullMetadataUrl,
        octree_url: octree_url,
      },
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedFile) {
      setErrorMessage("Please upload a valid file.");
      return;
    }

    try {
      setUploading(true);
      setErrorMessage("");
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("stope_id", stopeId);

      const response = await fetch(`${API_BASE_URL}/upload/`, {
        method: "POST",
        body: formData,
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Upload failed: ${errorText}`);
      }

      const data = await response.json();
      setUploadResponse(data);
      setShowTimeline(true);
      await saveStopeDetails(data, stopeId);
      //handleProcessingComplete(data);
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleViewFiles = () => {
    navigate("/search");
  };
  const saveStopeDetails = async (
    { file_id, volume, metadata_url, octree_url },
    name
  ) => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, "0");
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");
    const formattedDate = `${day}_${month}_${year}`;
    const formattedTime = `${hours}${minutes}${seconds}`;
    const shortname = name ? name.substring(0, 3).toUpperCase() : "XXX";
    const stopecode = shortname + "_" + formattedDate + "_" + formattedTime;
    const formattedDateTime = now.toISOString().slice(0, 19).replace("T", " ");

    const scanFileData = {
      actualFileURL: metadata_url,
      volume: volume,
      fileId: file_id,
      octree_url: octree_url,
      file_name: selectedFile.name,
    };
    let data = {
      bimsuid: 0,
      blastopeid: 0,
      companyid: "string",
      levelcode: 0,
      minecode: 0,
      planfilelocation: "[]",
      rockcode: 0,
      scancode: stopecode,
      scandatetime: formattedDateTime,
      scanfilelocation: JSON.stringify(scanFileData),
      scanid: 0,
      scanname: name,
      scanresult: "[]",
      stopecode: 0,
      userid: "string",
    };
    console.log(data);
    try {
      const response = await axios.post(
        API_BASE_URL + "/stopescannedresultinsertupdate",
        JSON.stringify(data),
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      console.log(response);
      data.scanid = response.data.id;
      localStorage.setItem("scanid", data.scanid);
      localStorage.setItem("stopedata", JSON.stringify(data));
      window.apiData = data;
    } catch (error) {
      console.error("API Error:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Fixed Header Section */}
      <header>
        <Header />
      </header>
      <div className="flex-none bg-white border-b shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between p-4 bg-white ">
            <h2 className="text-xl font-semibold text-gray-900">File Upload</h2>

            <div className="flex gap-3">
              <button
                onClick={() => navigate("/")}
                className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition"
                aria-label="Go to Home"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Home
              </button>
              <button
                onClick={handleViewFiles}
                className="flex items-center px-4 py-2 text-sm font-medium text-blue-700 bg-blue-100 rounded-md hover:bg-blue-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="View existing files"
              >
                View Existing Files
              </button>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-8 py-6 border-b border-gray-200">
              <div className="flex items-center justify-center">
                <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center">
                  <CloudUpload className="w-6 h-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Upload Stope File
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Upload your .las or .laz file for analysis
                  </p>
                </div>
              </div>
            </div>

            <div className="px-8 py-6">
              {errorMessage && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{errorMessage}</p>
                </div>
              )}

              {showTimeline ? (
                <ProcessingTimeline
                  file={selectedFile}
                  stopeId={stopeId}
                  uploadResponse={uploadResponse}
                  onProcessingComplete={handleProcessingComplete}
                />
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label
                      htmlFor="stopeId"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Stope ID
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        id="stopeId"
                        value={stopeId}
                        onChange={(e) => setStopeId(e.target.value)}
                        className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="Enter Stope ID"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Upload File
                    </label>
                    {!selectedFile ? (
                      <div className="mt-1">
                        <div className="flex justify-center px-6 py-8 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 transition-colors">
                          <div className="text-center">
                            <Upload className="mx-auto h-12 w-12 text-gray-400" />
                            <div className="mt-4 flex text-sm text-gray-600">
                              <label
                                htmlFor="fileUpload"
                                className="relative cursor-pointer rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none"
                              >
                                <span>Upload a file</span>
                                <input
                                  id="fileUpload"
                                  ref={fileInputRef}
                                  name="fileUpload"
                                  type="file"
                                  accept=".las,.laz"
                                  onChange={handleFileSelect}
                                  className="sr-only"
                                  required
                                />
                              </label>
                            </div>
                            <p className="text-xs text-gray-500 mt-2">
                              LAS or LAZ file
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-1 relative bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center">
                          <FileText className="h-8 w-8 text-blue-600" />
                          <div className="ml-4 flex-1">
                            <div className="text-sm font-medium text-gray-900">
                              {selectedFile.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {(selectedFile.size / (1024 * 1024)).toFixed(2)}{" "}
                              MB
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={removeFile}
                            className="p-2 rounded-full hover:bg-gray-200 transition-colors"
                          >
                            <X className="h-5 w-5 text-gray-500" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={uploading || !selectedFile}
                      className={`flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white
                        ${
                          uploading || !selectedFile
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        } 
                        transition-colors`}
                    >
                      {uploading ? (
                        <>
                          <Loader className="animate-spin -ml-1 mr-2 h-5 w-5" />
                          Uploading...
                        </>
                      ) : (
                        "Upload and Process File"
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>

            <div className="px-8 py-4 bg-gray-50 border-t border-gray-200">
              <p className="text-xs text-gray-500">
                By uploading a file, you agree to our{" "}
                <a href="#" className="text-blue-600 hover:text-blue-500">
                  Terms of Service
                </a>{" "}
                and
                <a href="#" className="text-blue-600 hover:text-blue-500">
                  {" "}
                  Privacy Policy
                </a>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default FileUploadPage;
